import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { mensagemDoErro } from '../api/cliente.js';
import { estoqueApi } from '../api/estoque.js';
import { invalidarDinheiro } from '../api/invalidacoes.js';
import { Botao, Campo } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { MOTIVO_DE_CANCELAMENTO, motivoValido } from './formatos.js';

/**
 * "Estornar" uma entrada de mercadoria: o estoque sai, o custo volta ao que
 * era e o dinheiro é desfeito (a saída realizada ganha a contrária; a prevista
 * é cancelada). Pede o motivo, no mesmo painel de `CorrecoesDaFatura`.
 *
 * O botão só aparece com `estoque:estornar`, e isso é COSMÉTICO: quem decide é
 * o servidor, que responde 403 a quem não tem a ação.
 */
export function EstornoDaEntrada({ entradaId }: { entradaId: string }) {
  const avisar = useToast();
  const cliente = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | undefined>();

  const estornar = useMutation({
    mutationFn: () => estoqueApi.estornarEntrada(entradaId, motivo.trim()),
    onSuccess: async () => {
      await Promise.all([
        cliente.invalidateQueries({ queryKey: ['estoque'] }),
        // A saída some do extrato (ou ganha a contrária), e o caixa pode mudar.
        invalidarDinheiro(cliente),
      ]);
    },
  });

  async function confirmar() {
    if (!motivoValido(motivo) || estornar.isPending) return;
    setErro(undefined);
    try {
      await estornar.mutateAsync();
      avisar('Entrada estornada. O estoque saiu e o dinheiro foi desfeito.');
      setAberto(false);
      setMotivo('');
    } catch (e) {
      setErro(mensagemDoErro(e));
    }
  }

  if (!aberto) {
    return (
      <Botao variante="contorno" pequeno onClick={() => setAberto(true)}>
        Estornar
      </Botao>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        background: 'var(--bg)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-sm)',
      }}
    >
      <Campo
        rotulo="Por que estornar a entrada?"
        ajuda={`De ${MOTIVO_DE_CANCELAMENTO.minimo} a ${MOTIVO_DE_CANCELAMENTO.maximo} caracteres. O estoque sai, o custo volta ao anterior e o dinheiro é desfeito.`}
        erro={erro}
      >
        <input
          value={motivo}
          maxLength={MOTIVO_DE_CANCELAMENTO.maximo}
          onChange={(e) => setMotivo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void confirmar();
            }
          }}
          aria-label="Motivo do estorno da entrada"
          autoFocus
        />
      </Campo>
      <div style={{ display: 'flex', gap: 8 }}>
        <Botao
          variante="fantasma"
          pequeno
          onClick={() => {
            setAberto(false);
            setErro(undefined);
          }}
        >
          Voltar
        </Botao>
        <Botao
          pequeno
          onClick={() => void confirmar()}
          disabled={!motivoValido(motivo) || estornar.isPending}
        >
          {estornar.isPending ? 'Estornando…' : 'Estornar entrada'}
        </Botao>
      </div>
    </div>
  );
}
